<template>
  <form class="rb-ob-form rb-screening-form" novalidate @submit.prevent="submit">
    <header>
      <template v-if="(loading || (!question && submitting)) && !snapshotGate">
        <h1 class="rb-ob-title">در حال آماده‌سازی…</h1>
      </template>
      <template v-else>
        <div class="rb-ob-progress" :aria-label="$t('intake.progressLabel')">
          <div class="rb-ob-progress-meta">
            <span>{{ progressLabel }}</span>
            <span>{{ phaseLabel }}</span>
          </div>
          <p class="rb-ob-q-hint">{{ $t('intake.screeningUi.adaptiveLength') }}</p>
        </div>
        <h1 class="rb-ob-title">{{ titleText }}</h1>
        <p v-if="question && question.question_id === 'S1'" class="rb-ob-lead">{{ $t('intake.screeningUi.estimate') }}</p>
        <p v-if="safetyNote" class="rb-ob-q-hint">{{ safetyNote }}</p>
        <p v-if="questionContext" class="rb-ob-status" role="note">{{ questionContext }}</p>
        <p v-if="screenHint" class="rb-ob-lead">{{ screenHint }}</p>
      </template>
    </header>

    <div v-if="snapshotGate" class="rb-ob-field-block">
      <p class="rb-ob-q">غربالگری قبلی آمادهٔ مچ است. ادامه می‌دهی یا از نو شروع می‌کنی؟</p>
      <div class="rb-ob-actions" style="margin-top: 12px">
        <button class="rb-ob-submit" type="button" :disabled="submitting" @click="goRematch">
          یافتن متخصص
        </button>
        <button
            class="rb-ob-submit rb-ob-submit-secondary"
            type="button"
            style="margin-top: 8px"
            :disabled="submitting"
            @click="restartScreening"
        >
          شروع دوباره
        </button>
      </div>
    </div>

    <div v-else-if="reviewReady" class="rb-ob-field-block">
      <h2>{{ $route.params.lang === 'en' ? 'Review your answers' : 'مرور پاسخ‌ها' }}</h2>
      <p>{{ $route.params.lang === 'en' ? 'Review your answers before finding a therapist. You can go back to make corrections.' : 'پیش از یافتن درمانگر، پاسخ‌هایت را مرور کن. برای اصلاح می‌توانی به سؤال‌ها برگردی.' }}</p>
      <article v-for="row in session.review || []" :key="row.id" class="rb-ob-field-block">
        <strong>{{ row.label }}</strong><p>{{ row.values.join(' · ') }}</p>
      </article>
      <p v-if="errorMessage" class="rb-ob-err is-visible" role="alert">{{ errorMessage }}</p>
      <div class="rb-ob-actions">
        <button type="button" class="rb-ob-submit" :disabled="submitting" @click="runFinalize(session.intake_id)">{{ $route.params.lang === 'en' ? 'Confirm and find a therapist' : 'تأیید و یافتن درمانگر' }}</button>
        <button type="button" class="rb-ob-submit rb-ob-submit-secondary" :disabled="submitting" @click="goPrev">{{ $route.params.lang === 'en' ? 'Edit answers' : 'اصلاح پاسخ‌ها' }}</button>
      </div>
    </div>
    <div v-else-if="apiDisabled" class="rb-ob-field-block">
      <p class="rb-ob-q">{{ $t('intake.screeningUi.unavailable') }}</p>
    </div>

    <div v-else-if="loading" class="rb-ob-field-block">
      <p class="rb-ob-q-hint">لطفاً صبر کنید…</p>
    </div>

    <div v-else-if="question" class="rb-ob-field-block" :class="{ 'is-invalid': !!errorMessage }">
      <aside v-if="requiresRespondentConfirmation" class="rb-ob-status" role="note">
        <p>{{ $route.params.lang === 'en' ? 'This part asks about the adolescent receiving care. In a private setting, let them read and answer for themselves. A parent should not answer these first-person items about their own experience.' : 'این بخش دربارهٔ نوجوانی است که قرار است درمان دریافت کند. در محیطی خصوصی، خود نوجوان سؤال‌ها را بخواند و پاسخ دهد. والد نباید این سؤال‌های اول‌شخص را دربارهٔ تجربهٔ خودش پاسخ دهد.' }}</p>
        <label><input v-model="respondentConfirmed" type="checkbox"> {{ $route.params.lang === 'en' ? 'The adolescent is answering for themselves in a private setting.' : 'خود نوجوان در محیط خصوصی پاسخ می‌دهد.' }}</label>
      </aside>
      <ScreeningQuestionRenderer
          v-if="!requiresRespondentConfirmation || respondentConfirmed"
          :key="formKey"
          ref="renderer"
          :question="question"
          :busy="submitting || loading"
          :can-go-back="!!(session && session.can_go_back !== false && Number(session.client_seq || 0) > 0)"
          :s1-intent="s1Intent"
          :service-track-id="session && session.service_track_id ? session.service_track_id : null"
          @change="onAnswerChange"
          @contract-state="onQuestionContractState"
          @retry="retryQuestion"
          @back="goPrev"
      />
      <p class="rb-ob-err" :class="{ 'is-visible': !!errorMessage }" role="alert">
        {{ errorMessage }}
      </p>
    </div>

    <div v-else-if="errorMessage" class="rb-ob-field-block">
      <p class="rb-ob-err is-visible" role="alert">{{ errorMessage }}</p>
      <button type="button" class="rb-ob-submit rb-ob-submit-secondary" @click="bootstrap">{{ $route.params.lang === 'en' ? 'Try again' : 'بررسی دوباره' }}</button>
    </div>

    <div v-if="!snapshotGate && !reviewReady && !loading && !apiDisabled" class="rb-ob-actions">
      <button
          class="rb-ob-submit"
          type="submit"
          :disabled="submitting || !question || !questionContractReady || !!questionContractIssue || (requiresRespondentConfirmation && !respondentConfirmed)"
          :class="{ 'is-loading': submitting }"
      >
        {{ $t('intake.continue') }}
      </button>
      <p class="rb-ob-status" role="status" aria-live="polite">{{ formStatus }}</p>
      <button class="rb-ob-submit rb-ob-submit-secondary" type="button" :disabled="submitting" @click="confirmRestart">{{ $t('intake.screeningUi.restart') }}</button>
      <div class="rb-ob-trust">
        <p>{{ $t('intake.trustPath') }}</p>
        <p>{{ $t('authUi.familyNote') }}</p>
      </div>
    </div>
  </form>
</template>

<script>
import ScreeningQuestionRenderer from '@/intake/ScreeningQuestionRenderer.vue'
import intakeFormMixin from '@/intake/intakeFormMixin'
import {EventBus} from '@/event-bus'
import {markIntakePending} from '@/intake/intakeState'
import {
  isMatchmakingV2ApiEnabled,
  startScreeningSession,
  getScreeningSession,
  reviseScreeningPreferences,
  prevScreening,
  finalizeScreening,
} from '@/intake/sandboxMatchApi'
import {
  loadScreeningStore,
  applySessionToStore,
  setFinalizedSnapshot,
  divertRouteForSession,
  divertRouteForStatus,
  isMatchReadyStatus,
  restartScreeningStore,
  setDispMoreVariantFromAnswer,
} from '@/intake/screeningSession'
import {postScreeningAnswer} from '@/intake/screeningAnswer'
import {toFaDigits} from '@/intake/intakeState'
// Active write model: one server-authoritative next_question answer per Continue.
// answers/batch remains for service/tests; do not reintroduce composed client batches.
import {extractNeedLabel} from '@/intake/screeningCatalog'
import {invalidateMatchSession, loadMatchSession} from '@/intake/sandboxMatchSession'
import {countdownToSession, fetchActiveBookingStatus, fetchActiveClinicalResponse, fetchOwnedSession, hasAuthToken} from '@/intake/portalApi'

const PHASE_BY_LAYOUT = {
  participant_select: 'participants',
  age_inputs: 'participants',
  grouped_multi_select: 'topics',
  availability_grid: 'availability',
  integer_scale: 'severity',
}

const TITLE_BY_QID_PREFIX = {
  S: null,
  P: null,
  U: 'کمک به روشن‌شدن موضوع',
  V: 'شدت موضوع',
  C: 'پیچیدگی و سابقه',
  R: 'ایمنی',
  O: 'زمان و ترجیحات',
}

export default {
  name: 'IntakeScreeningStep',
  components: {ScreeningQuestionRenderer},
  mixins: [intakeFormMixin],
  data() {
    return {
      loading: true,
      submitting: false,
      snapshotGate: false,
      apiDisabled: false,
      session: null,
      question: null,
      answerPayload: {valid: false, answer_ids: []},
      s1Intent: null,
      errorMessage: '',
      formStatus: '',
      formEpoch: 0,
      screeningDisposed: false,
      respondentConfirmed: false,
      respondentContext: '',
      questionContractReady: true,
      questionContractIssue: null,
      questionRetryRequestId: 0,
    }
  },
  computed: {
    requiresRespondentConfirmation() {
      return !!(this.question && this.question.respondent_confirmation_required)
    },
    reviewReady() {
      return !this.loading && !!this.session && this.session.finalize_ready && !this.question && this.session.state !== 'FINALIZED'
    },
    formKey() {
      const qid = this.question ? this.question.question_id : 'x'
      const seq = this.session ? Number(this.session.client_seq || 0) : 0
      const intent = this.question && this.question.question_id === 'S2'
        ? (this.s1Intent || '')
        : ''
      return qid + '-' + seq + '-' + this.formEpoch + '-' + intent
    },
    answeredCount() {
      const count = Number(this.session && this.session.answered_question_count)
      return Number.isSafeInteger(count) && count >= 0 ? count : 0
    },
    progressLabel() {
      const digit = this.$route.params.lang === 'en' ? String : toFaDigits
      return this.$t('intake.screeningUi.answeredCount', {count: digit(this.answeredCount)})
    },
    questionContext() {
      const context = this.question && this.question.display_context
      const questionId = this.question && this.question.question_id
      if ((context && context.kind === 'CONCERN_CLARIFICATION') || ['U1', 'U2', 'U3'].includes(questionId)) {
        return this.$t('intake.screeningUi.clarificationContext')
      }
      if (!context) return ''
      const labels = Array.isArray(context.topic_labels) ? context.topic_labels.filter(label => typeof label === 'string' && label.trim()) : []
      if (context.kind === 'TOPIC' && labels.length) {
        return this.$t('intake.screeningUi.topicContext', {topics: labels.join(' / ')})
      }
      const keys = {
        GENERAL_WELLBEING: 'wellbeingContext', HISTORY_CURRENT_CARE: 'historyContext',
        SAFETY: 'safetyContext', AVAILABILITY_PREFERENCES: 'preferencesContext', SERVICE: 'serviceContext',
      }
      return keys[context.kind] ? this.$t('intake.screeningUi.' + keys[context.kind]) : ''
    },
    phaseLabel() {
      const layout = this.question && this.question.layout
      if (layout === 'participant_select' || layout === 'age_inputs') {
        return this.$t('intake.stepLabels.participants')
      }
      if (layout === 'grouped_multi_select') {
        return this.$t('intake.stepLabels.topics')
      }
      if (this.question && this.question.module_kind) return this.$t('intake.screeningUi.screening')
      if (PHASE_BY_LAYOUT[layout]) return this.$t('intake.stepLabels.' + PHASE_BY_LAYOUT[layout])
      const qid = (this.question && this.question.question_id) || ''
      if (qid.charAt(0) === 'S') {
        return this.$t('intake.stepLabels.participants')
      }
      if (qid.charAt(0) === 'P') {
        return this.$t('intake.stepLabels.topics')
      }
      return TITLE_BY_QID_PREFIX[qid.charAt(0)] || this.$t('intake.screeningUi.screening')
    },
    titleText() {
      if (this.question && this.question.module_kind) return this.$t('intake.screeningUi.screening')
      const layout = this.question && this.question.layout
      const qid = (this.question && this.question.question_id) || ''
      if (layout === 'participant_select' || layout === 'age_inputs' || qid.charAt(0) === 'S') {
        return this.$t('intake.serviceTitle')
      }
      if (layout === 'grouped_multi_select' || qid === 'P1') {
        return this.$t('intake.topicsTitle')
      }
      if (qid === 'P2') {
        return 'اولویت'
      }
      if (qid === 'P3') {
        return 'ارتباط موضوع‌ها'
      }
      return TITLE_BY_QID_PREFIX[qid.charAt(0)] || this.$t('intake.serviceTitle')
    },
    screenHint() {
      const qid = (this.question && this.question.question_id) || ''
      if (qid.charAt(0) === 'V') {
        return 'برای موضوع جاری به چند سوال کوتاه پاسخ بده (در صورت چند موضوع، این بخش تکرار می‌شود).'
      }
      if (qid.charAt(0) === 'C') {
        return 'چند سوال دربارهٔ سابقه و شرایط فعلی.'
      }
      if (qid.charAt(0) === 'O') {
        return 'زمان‌های ممکن و ترجیحات درمانگر.'
      }
      return ''
    },
    safetyNote() {
      if ((this.session && this.session.safety_checkpoint)
          || (this.question && Array.isArray(this.question.labels) && this.question.labels.includes('SAFETY'))) {
        return this.$t('intake.screeningUi.safetyIntro')
      }
      return ''
    },
  },
  created() {
    if (!isMatchmakingV2ApiEnabled()) {
      this.apiDisabled = true
      this.loading = false
      this.errorMessage = 'Matchmaking V2 API is required for /fa/intake'
      return
    }
    this.bootstrap()
  },
  mounted() {
    EventBus.$on('intake-screening-back', this.goPrev)
  },
  beforeDestroy() {
    this.screeningDisposed = true
    EventBus.$off('intake-screening-back', this.goPrev)
  },
  methods: {
    isScreeningActive() {
      return !this.screeningDisposed && this.$route.name === 'intakeScreening'
    },
    confirmRestart() {
      if (window.confirm(this.$t('intake.screeningUi.restartConfirm'))) this.restartScreening()
    },
    redirectAuth() {
      if (!this.isScreeningActive()) return
      markIntakePending()
      this.$router.replace({
        name: 'login',
        params: {lang: this.$route.params.lang || 'fa'},
        query: {afterLogin: 'intakeScreening'},
      })
    },
    wantsRestart() {
      const q = this.$route.query || {}
      const v = String(q.restart || '').toLowerCase()
      return v === '1' || v === 'true' || v === 'yes'
    },
    async resumeExistingFirstBooking() {
      // Reopening the ordinary intake entry should resume an existing first
      // appointment, not silently present another first-session checkout.
      // An explicit fresh intake/preferences edit and a clinical rematch keep
      // their existing paths; this is not an episode-wide booking restriction.
      if (!hasAuthToken() || this.wantsRestart() || this.$route.query.revise_preferences === '1') return false
      const lookupError = error => {
        if (error && (error.status === 401 || error.code === 'AUTH_REQUIRED')) return error
        return new Error(this.$route.params.lang === 'en'
          ? 'Your existing booking status could not be checked. Please try again before starting another booking.'
          : 'وضعیت رزرو قبلی دریافت نشد. پیش از شروع رزرو دوباره، وضعیت را مجدداً بررسی کن.')
      }
      let active, clinical
      try {
        active = await fetchActiveBookingStatus()
        if (!this.isScreeningActive()) return true
        if (active.client_status === 'MANUAL_SCHEDULING_REQUIRED') {
          this.$router.replace({name:'portalHome',params:{lang:this.$route.params.lang || 'fa'}})
          return true
        }
        if (!['HOLD_ACTIVE', 'AWAITING_PAYMENT', 'PROCESSING', 'CONFIRMED'].includes(active.client_status)) return false
        clinical = await fetchActiveClinicalResponse()
        if (!this.isScreeningActive()) return true
        if (clinical && clinical.rematch) return false
      } catch (error) {
        throw lookupError(error)
      }
      const booking = active.booking || {}
      const lang = this.$route.params.lang || 'fa'
      let target = null
      if (active.client_status === 'CONFIRMED') {
        const episode = clinical && clinical.episode
        const id = String(booking.user_session_id || '')
        if (!episode || !/^\d+$/.test(id) || String(episode.first_user_session_id) !== id
          || Number(episode.held_session_count) !== 0 || !['PREPARING', 'ACTIVE'].includes(episode.state)) return false
        let session
        try { session = await fetchOwnedSession(id) } catch (error) { throw lookupError(error) }
        if (!this.isScreeningActive()) return true
        const countdown = countdownToSession(session)
        if (!session || session.deleted_at || session.state !== 'date_fixed' || !countdown || countdown.overdue) return false
        target = {name: 'portalSessionDetail', params: {lang}, query: {id}}
      } else if (active.client_status === 'PROCESSING' && booking.booking_id) {
        target = {name: 'intakePayProcessing', params: {lang}, query: {bookingId: booking.booking_id}}
      } else if (active.client_status === 'AWAITING_PAYMENT' && booking.cart_id) {
        target = {name: 'portalReceipt', params: {lang}, query: {id: String(booking.cart_id)}}
      } else if (active.client_status === 'HOLD_ACTIVE') {
        const hold = active.hold
        const match = loadMatchSession() || {}
        // Checkout already verifies/reuses this exact recommendation and slot.
        if (hold && hold.recommendation_id && hold.slot_id && match.recommendationId && match.selectedSlotId
          && String(hold.recommendation_id) === String(match.recommendationId)
          && String(hold.slot_id) === String(match.selectedSlotId)) {
          target = {name: 'intakeCheckout', params: {lang}}
        } else {
          // Another browser may own the local selection; show server recovery
          // guidance rather than inventing enough context to pay for its hold.
          target = {name: 'portalPayments', params: {lang}}
        }
      }
      if (!target) return false
      await this.$router.replace(target)
      return true
    },
    goRematch() {
      this.$router.replace({name: 'intakeMatching'})
    },
    onAnswerChange(payload) {
      this.answerPayload = payload || {valid: false, answer_ids: []}
      if (this.question && this.question.question_id === 'S1'
          && payload && payload.answer_ids && payload.answer_ids[0]) {
        this.s1Intent = payload.answer_ids[0]
      }
      if (this.errorMessage) {
        this.errorMessage = ''
        this.formStatus = ''
      }
    },
    onQuestionContractState(state) {
      if (!state || !this.question || state.question_id !== this.question.question_id
          || String(state.instance_key || '') !== String(this.question.instance_key || '')) return
      this.questionContractReady = true
      this.questionContractIssue = state.issue || null
      if (this.questionContractIssue) {
        this.answerPayload = {valid: false, answer_ids: []}
        this.errorMessage = ''
        this.formStatus = ''
      }
    },
    async retryQuestion() {
      if (this.loading || this.submitting || !this.questionContractIssue || !this.session || !this.isScreeningActive()) return
      const intakeId = this.session.intake_id
      if (!intakeId) return
      const epoch = this.formEpoch
      const requestId = ++this.questionRetryRequestId
      this.submitting = true
      this.errorMessage = ''
      this.formStatus = this.$t('intake.screeningUi.retryingQuestion')
      try {
        // GET rebuilds the current question. Never replay an old answer receipt
        // or create a new intake just because its saved response lacked options.
        const fresh = await getScreeningSession(intakeId)
        if (!this.isScreeningActive() || requestId !== this.questionRetryRequestId
            || epoch !== this.formEpoch || String(this.session && this.session.intake_id) !== String(intakeId)) return
        if (!fresh || String(fresh.intake_id) !== String(intakeId)) {
          throw new Error('SCREENING_RESPONSE_MISMATCH')
        }
        this.formStatus = ''
        this.applySession(fresh)
        await this.handleSessionSideEffects(fresh)
      } catch (error) {
        if (!this.isScreeningActive() || requestId !== this.questionRetryRequestId
            || epoch !== this.formEpoch || String(this.session && this.session.intake_id) !== String(intakeId)) return
        if (error && (error.status === 401 || error.code === 'AUTH_REQUIRED')) {
          this.redirectAuth()
          return
        }
        this.errorMessage = error && error.status === 429 && error.message
          ? String(error.message) : this.$t('intake.screeningUi.retryQuestionFailed')
        this.formStatus = this.errorMessage
      } finally {
        if (requestId === this.questionRetryRequestId) this.submitting = false
      }
    },
    async restartScreening() {
      if (this.loading || this.submitting) return
      this.snapshotGate = false
      invalidateMatchSession(loadScreeningStore())
      restartScreeningStore()
      this.respondentConfirmed = false
      this.respondentContext = ''
      this.s1Intent = null
      this.loading = true
      this.errorMessage = ''
      try {
        const session = await startScreeningSession('IR', {forceNew: true})
        this.applySession(session, {snapshotId: null, s1Intent: null, needLabel: null})
        if (await this.handleSessionSideEffects(session)) {
          return
        }
        if (this.$route.query.restart) {
          this.$router.replace({
            name: 'intakeScreening',
            params: {lang: this.$route.params.lang || 'fa'},
            query: {},
          })
        }
      } catch (e) {
        if (e && (e.status === 401 || e.code === 'AUTH_REQUIRED')) {
          this.redirectAuth()
          return
        }
        this.errorMessage = (e && e.message) ? String(e.message) : 'screening start failed'
      } finally {
        this.loading = false
      }
    },
    async bootstrap() {
      this.loading = true
      this.errorMessage = ''
      this.snapshotGate = false
      try {
        if (await this.resumeExistingFirstBooking()) return
        if (!this.isScreeningActive()) return
        const stored = loadScreeningStore()
        this.s1Intent = stored.s1Intent || null
        if (stored.intakeId && this.$route.query.revise_preferences === '1') {
          const revised = await reviseScreeningPreferences(stored.intakeId)
          if (!this.isScreeningActive()) return
          invalidateMatchSession(stored)
          this.applySession(revised, {snapshotId: null})
          this.$router.replace({name:'intakeScreening',params:{lang:this.$route.params.lang || 'fa'}})
          return
        }

        if (stored.snapshotId && !this.wantsRestart()) {
          // Cached completion is not proof the server still has this intake or
          // its immutable snapshot. Revalidate before offering matching again.
          let verified
          try {
            verified = stored.intakeId ? await getScreeningSession(stored.intakeId) : null
          } catch (error) {
            if (!this.isScreeningActive()) return
            if (!(error && (error.status === 404 || error.code === 'INTAKE_NOT_FOUND'))) throw error
          }
          if (!this.isScreeningActive()) return
          if (verified) {
            this.applySession(verified, {snapshotId: verified.client_requirement_snapshot_id || null})
            if (divertRouteForSession(verified) || divertRouteForStatus(verified.status || verified.sm_state)) {
              await this.handleSessionSideEffects(verified)
              return
            }
            if (verified.state === 'FINALIZED' && verified.client_requirement_snapshot_id
                && isMatchReadyStatus(verified.status || verified.sm_state)) {
              this.snapshotGate = true
              return
            }
            if (verified.state === 'IN_PROGRESS') {
              invalidateMatchSession(stored)
              await this.handleSessionSideEffects(verified)
              return
            }
          }
          // Missing intake/snapshot cannot be recovered by replaying the old
          // cached start key. A fresh intake still obeys account safety holds.
          invalidateMatchSession(stored)
          restartScreeningStore()
          this.s1Intent = null
        }
        if (this.wantsRestart()) {
          invalidateMatchSession(stored)
          restartScreeningStore()
          this.s1Intent = null
        }

        let session
        const freshStore = loadScreeningStore()
        if (freshStore.intakeId && !freshStore.snapshotId) {
          try {
            session = await getScreeningSession(freshStore.intakeId)
          } catch (e) {
            if (!this.isScreeningActive()) return
            if (!(e && (e.status === 404 || e.code === 'INTAKE_NOT_FOUND'))) throw e
            // Resume failed (e.g. LEDGER_REPLAY_FAILED / FINALIZED). Clear the
            // cached start Idempotency-Key so we mint a real new intake instead
            // of replaying the stale start response for the broken one.
            restartScreeningStore()
            this.s1Intent = null
            session = await startScreeningSession('IR', {forceNew: true})
            if (!this.isScreeningActive()) return
            applySessionToStore(session, {snapshotId: null, s1Intent: null, needLabel: null})
          }
        } else {
          session = await startScreeningSession('IR')
          if (!this.isScreeningActive()) return
          applySessionToStore(session, {snapshotId: null, s1Intent: null, needLabel: null})
          this.s1Intent = null
        }
        this.applySession(session)
        if (await this.handleSessionSideEffects(session)) {
          return
        }
        // Restart is a one-shot action. Keeping it in the URL would discard
        // the new session again whenever the user refreshes this page.
        if (this.wantsRestart()) {
          const query = Object.assign({}, this.$route.query)
          delete query.restart
          this.$router.replace({
            name: 'intakeScreening',
            params: {lang: this.$route.params.lang || 'fa'},
            query,
          })
        }
      } catch (e) {
        if (e && (e.status === 401 || e.code === 'AUTH_REQUIRED')) {
          this.redirectAuth()
          return
        }
        this.errorMessage = (e && e.message) ? String(e.message) : 'screening start failed'
      } finally {
        this.loading = false
      }
    },
    applySession(session, extras) {
      if (!this.isScreeningActive()) return
      this.session = session
      this.question = (session && session.next_question)
        ? Object.assign({}, session.next_question, {answer_prefill: session.answer_prefill || null})
        : null
      this.questionContractReady = !this.question
      this.questionContractIssue = null
      const respondentContext = this.question && this.question.respondent_confirmation_required
        ? [session.intake_id,this.question.focal_participant_ref,this.question.respondent_mode].join(':') : ''
      if (respondentContext !== this.respondentContext) this.respondentConfirmed = false
      this.respondentContext = respondentContext
      this.answerPayload = {valid: false, answer_ids: []}

      if (session && Object.prototype.hasOwnProperty.call(session, 's1_intent')) {
        this.s1Intent = session.s1_intent || null
      } else if (this.question && this.question.s1_intent) {
        this.s1Intent = this.question.s1_intent
      } else if (extras && Object.prototype.hasOwnProperty.call(extras, 's1Intent')) {
        this.s1Intent = extras.s1Intent || null
      }

      if (this.question && this.question.question_id === 'S2' && this.s1Intent) {
        this.question.s1_intent = this.s1Intent
      }

      const patch = Object.assign({}, extras || {})
      patch.s1Intent = this.s1Intent
      const needFromQ = this.question ? extractNeedLabel(this.question.text) : null
      if (needFromQ) {
        patch.needLabel = needFromQ
      }
      applySessionToStore(session, patch)
      this.formEpoch += 1
    },
    async handleSessionSideEffects(session) {
      // A response can arrive after navigation. It must not pull the user back
      // from the portal into a screening disposition or matching screen.
      if (!this.isScreeningActive()) return true
      const divert = divertRouteForSession(session)
      if (divert) {
        invalidateMatchSession({intakeId:session.intake_id,snapshotId:session.client_requirement_snapshot_id})
        this.$router.replace({
          name: divert.name,
          params: Object.assign(
            {lang: this.$route.params.lang || 'fa'},
            divert.params || {},
          ),
        })
        return true
      }
      if (session && session.state === 'FINALIZED') {
        const snapId = session.client_requirement_snapshot_id
        const status = session.status || session.sm_state
        const statusDivert = divertRouteForStatus(status)
        if (statusDivert) {
          this.$router.replace({
            name: statusDivert.name,
            params: Object.assign(
              {lang: this.$route.params.lang || 'fa'},
              statusDivert.params || {},
            ),
          })
          return true
        }
        if (snapId && isMatchReadyStatus(status)) {
          setFinalizedSnapshot(snapId, session.intake_id)
          this.$router.replace({name: 'intakeMatching'})
          return true
        }
        await this.runFinalize(session.intake_id)
        return true
      }
      if (session && session.finalize_ready && !session.next_question) {
        return false
      }
      return false
    },
    collectAnswer() {
      const renderer = this.$refs.renderer
      const payload = (renderer && renderer.getAnswer)
        ? renderer.getAnswer()
        : (this.answerPayload || {valid: false, answer_ids: []})
      if (!this.question || !this.question.question_id) {
        return null
      }
      const out = {
        question_id: this.question.question_id,
        answer_ids: (payload && payload.answer_ids) || [],
        valid: !!(payload && payload.valid),
      }
      if (this.requiresRespondentConfirmation) {
        out.valid = out.valid && this.respondentConfirmed
        if (this.respondentConfirmed) out.respondent_confirmation = 'FOCAL_PARTICIPANT_SELF'
      }
      if (payload && payload.ages) {
        out.ages = payload.ages
      }
      if (this.question.participant_ref) {
        out.participant_ref = this.question.participant_ref
      }
      return out
    },
    async submit() {
      if (this.submitting || this.snapshotGate || !this.question || !this.questionContractReady || this.questionContractIssue) {
        return
      }
      const collected = this.collectAnswer()
      if (!collected || !collected.valid) {
        this.errorMessage = this.$t('intake.fieldRequired')
        this.formStatus = this.$t('intake.formIncomplete')
        this.noteInvalid()
        return
      }

      if (collected.question_id === 'S1' && collected.answer_ids[0]) {
        this.s1Intent = collected.answer_ids[0]
      }

      this.submitting = true
      this.errorMessage = ''
      this.formStatus = ''
      try {
        const expectedId = this.question.question_id
        if (collected.question_id !== expectedId) {
          const fresh = await getScreeningSession(this.session.intake_id)
          this.applySession(fresh)
          this.errorMessage = this.$t('intake.fieldRequired')
          this.formStatus = this.$t('intake.formIncomplete')
          this.noteInvalid()
          return
        }

        const result = await postScreeningAnswer(this.session.intake_id, collected)
        if (!this.isScreeningActive()) return
        const session = result.session
        if (!session) {
          throw new Error('empty session')
        }
        setDispMoreVariantFromAnswer(collected.question_id, collected.answer_ids)
        this.applySession(session)
        if (await this.handleSessionSideEffects(session)) {
          return
        }
      } catch (e) {
        if (e && (e.status === 401 || e.code === 'AUTH_REQUIRED')) {
          this.redirectAuth()
          return
        }
        if (e && (e.code === 'INTAKE_FINALIZED' || e.code === 'LEDGER_REPLAY_FAILED')) {
          this.formStatus = this.$t('intake.screeningUi.unavailable')
          this.errorMessage = this.formStatus
          return
        }
        if (e && (e.code === 'SCHEMA_INVALID' || e.status === 400)) {
          try {
            const fresh = await getScreeningSession(this.session.intake_id)
            this.applySession(fresh)
          } catch (ignore) {
            // keep original error
          }
        }
        this.errorMessage = (e && e.message) ? String(e.message) : 'answer failed'
        this.formStatus = this.errorMessage
        this.noteInvalid()
      } finally {
        this.submitting = false
      }
    },
    async goPrev() {
      if (this.snapshotGate) {
        this.$router.push({name: 'portalHome', params: {lang: this.$route.params.lang || 'fa'}})
        return
      }
      if (this.submitting || this.loading) {
        return
      }
      const seq = this.session ? Number(this.session.client_seq || 0) : 0
      if (seq <= 0 || this.session.can_go_back === false) {
        this.$router.push({name: 'portalHome', params: {lang: this.$route.params.lang || 'fa'}})
        return
      }
      this.submitting = true
      this.errorMessage = ''
      try {
        const session = await prevScreening(this.session.intake_id)
        this.applySession(session)
      } catch (e) {
        if (e && (e.status === 401 || e.code === 'AUTH_REQUIRED')) {
          this.redirectAuth()
          return
        }
        this.errorMessage = (e && e.message) ? String(e.message) : 'prev failed'
      } finally {
        this.submitting = false
      }
    },
    async runFinalize(intakeId) {
      this.submitting = true
      try {
        const snap = await finalizeScreening(intakeId)
        if (!this.isScreeningActive()) return
        const status = snap.status
        // Backend reopened an interactive clarification (e.g. C6=UNSURE) instead of
        // finalizing into MORE_ANSWERS with no next_question.
        if (status === 'CLARIFICATION_REQUIRED' || snap.clarification_required) {
          this.applySession(snap)
          return
        }
        const divert = divertRouteForStatus(status)
        if (divert) {
          applySessionToStore(Object.assign({}, snap, {
            state: snap.state || 'FINALIZED',
            status,
            terminal_status: snap.terminal_status || status,
          }))
          this.$router.replace({
            name: divert.name,
            params: Object.assign(
              {lang: this.$route.params.lang || 'fa'},
              divert.params || {},
            ),
          })
          return
        }
        if (!isMatchReadyStatus(status) || !snap.client_requirement_snapshot_id) {
          this.errorMessage = status || 'NOT_MATCH_READY'
          return
        }
        setFinalizedSnapshot(snap.client_requirement_snapshot_id, intakeId)
        this.$router.replace({name: 'intakeMatching'})
      } catch (e) {
        if (e && (e.status === 401 || e.code === 'AUTH_REQUIRED')) {
          this.redirectAuth()
          return
        }
        this.errorMessage = (e && e.message) ? String(e.message) : 'finalize failed'
      } finally {
        this.submitting = false
      }
    },
  },
}
</script>
